import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, NgZone, inject } from '@angular/core';
import { LocalizationService } from '@abp/ng.core';
import {
  TAB_NOTIFICATION_STATE,
  TabNotificationState,
} from '../models/tab-notification-state.type';

const BLINK_INTERVAL_MS = 1000;

const VIBRATE_PATTERN: readonly number[] = [300, 200, 300];
const VIBRATE_EVERY_N_TICKS = 2;

const SCHEDULED_CALL_OFFER_TITLE_KEY = 'AgentDesktop::ScheduledCall:TabTitle';
const SCHEDULED_CALL_OFFER_TITLE_FALLBACK = 'Reschedule Assigned';

@Injectable({ providedIn: 'root' })
export class TabNotificationService {
  private readonly document = inject(DOCUMENT);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly l10n = inject(LocalizationService);

  private readonly defaultTitle = this.document.title;

  private state: TabNotificationState = TAB_NOTIFICATION_STATE.Default;
  private blinkTimer: ReturnType<typeof setInterval> | null = null;
  private blinkTicks = 0;

  private iconLink: HTMLLinkElement | null = null;
  private createdIconLink = false;
  private originalIconHref: string | null = null;
  private originalIconType: string | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => this.reset());
  }

  startIncomingCall(): void {
    if (this.state === TAB_NOTIFICATION_STATE.Incoming) {
      return;
    }

    this.state = TAB_NOTIFICATION_STATE.Incoming;
    this.blinkTicks = 0;
    this.document.title = this.incomingTitle();
    this.applyFavicon(INCOMING_CALL_FAVICON, 'image/svg+xml');
    this.startBlink();
  }

  setActiveCall(): void {
    if (this.state === TAB_NOTIFICATION_STATE.Active) {
      return;
    }

    this.state = TAB_NOTIFICATION_STATE.Active;
    this.stopBlink();
    this.document.title = this.activeCallTitle();
    this.applyFavicon(ACTIVE_CALL_FAVICON, 'image/svg+xml');
  }

  /** Background-tab mark for an offered callback (154880/6.2); CallTabSyncService decides when it applies. */
  showScheduledCallOffer(): void {
    if (this.state === TAB_NOTIFICATION_STATE.ScheduledCallOffer) {
      return;
    }

    this.state = TAB_NOTIFICATION_STATE.ScheduledCallOffer;
    this.stopBlink();
    this.document.title = this.scheduledCallOfferTitle();
    this.applyFavicon(SCHEDULED_CALL_OFFER_FAVICON, 'image/svg+xml');
  }

  reset(): void {
    if (this.state === TAB_NOTIFICATION_STATE.Default) {
      return;
    }

    this.state = TAB_NOTIFICATION_STATE.Default;
    this.stopBlink();
    this.document.title = this.defaultTitle;
    this.restoreFavicon();
  }

  private startBlink(): void {
    if (this.blinkTimer !== null) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.blinkTimer = setInterval(() => {
        this.blinkTicks++;
        this.document.title =
          this.blinkTicks % 2 === 0 ? this.incomingTitle() : this.defaultTitle;

        if (this.blinkTicks % VIBRATE_EVERY_N_TICKS === 0) {
          this.vibrate();
        }
      }, BLINK_INTERVAL_MS);
    });

    this.vibrate();
  }

  private stopBlink(): void {
    if (this.blinkTimer !== null) {
      clearInterval(this.blinkTimer);
      this.blinkTimer = null;
    }
    this.blinkTicks = 0;
  }

  private vibrate(): void {
    const navigatorRef = this.document.defaultView?.navigator;
    if (typeof navigatorRef?.vibrate !== 'function') {
      return;
    }

    try {
      navigatorRef.vibrate([...VIBRATE_PATTERN]);
    } catch {
    }
  }

  private applyFavicon(href: string, type: string): void {
    const link = this.resolveIconLink();
    if (!link) {
      return;
    }

    if (this.originalIconHref === null && !this.createdIconLink) {
      this.originalIconHref = link.getAttribute('href');
      this.originalIconType = link.getAttribute('type');
    }

    link.setAttribute('type', type);
    link.setAttribute('href', href);
  }

  private restoreFavicon(): void {
    const link = this.iconLink;
    if (!link) {
      return;
    }

    if (this.createdIconLink) {
      link.remove();
      this.iconLink = null;
      this.createdIconLink = false;
      return;
    }

    if (this.originalIconHref === null) {
      return;
    }

    link.setAttribute('href', this.originalIconHref);
    if (this.originalIconType === null) {
      link.removeAttribute('type');
    } else {
      link.setAttribute('type', this.originalIconType);
    }
  }

  private resolveIconLink(): HTMLLinkElement | null {
    if (this.iconLink?.isConnected) {
      return this.iconLink;
    }

    const existing = this.document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
    if (existing) {
      this.iconLink = existing;
      this.createdIconLink = false;
      return existing;
    }

    const head = this.document.head;
    if (!head) {
      return null;
    }

    const created = this.document.createElement('link');
    created.setAttribute('rel', 'icon');
    head.appendChild(created);
    this.iconLink = created;
    this.createdIconLink = true;
    return created;
  }

  private activeCallTitle(): string {
    return `${this.localize('TabTitleActiveCall', 'Active Call')} | ${this.defaultTitle}`;
  }

  private incomingTitle(): string {
    return `${this.localize('TabTitleIncomingCall', 'Incoming Call')} | ${this.defaultTitle}`;
  }

  private scheduledCallOfferTitle(): string {
    const value = this.l10n.instant(SCHEDULED_CALL_OFFER_TITLE_KEY, this.defaultTitle);
    return value && value !== SCHEDULED_CALL_OFFER_TITLE_KEY
      ? value
      : `${SCHEDULED_CALL_OFFER_TITLE_FALLBACK} | ${this.defaultTitle}`;
  }

  private localize(suffix: string, fallback: string): string {
    const key = `AgentDesktop::Telephony:${suffix}`;
    const value = this.l10n.instant(key);
    return value && value !== key ? value : fallback;
  }
}

const PHONE_GLYPH_PATH =
  'M160.2 25C152.3 6.1 131.7-3.9 112.1 1.4l-5.5 1.5c-64.6 17.6-119.8 80.2-103.7 '
  + '156.4 37.1 175 174.8 312.7 349.8 349.8 76.3 16.2 138.8-39.1 156.4-103.7l1.5-5.5c5.4-19.7'
  + '-4.7-40.3-23.5-48.1l-97.3-40.5c-16.5-6.9-35.6-2.1-47 11.8l-38.6 47.2C233.9 335.4 177.3 '
  + '277 144.8 205.3L189 169.3c13.9-11.3 18.6-30.4 11.8-47L160.2 25z';

const INCOMING_CALL_FAVICON = buildPhoneFavicon('#2563eb');
const ACTIVE_CALL_FAVICON = buildPhoneFavicon('#16a34a');
// Favicon data URIs cannot read CSS tokens; same literal as --acx-amber-600 behind --acx-scheduled-call.
const SCHEDULED_CALL_OFFER_FAVICON = buildPhoneFavicon('#d97706');

function buildPhoneFavicon(discColor: string): string {
  const svg =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">'
    + `<circle cx="256" cy="256" r="256" fill="${discColor}"/>`
    + `<g transform="translate(128 128) scale(0.5)"><path fill="#ffffff" d="${PHONE_GLYPH_PATH}"/></g>`
    + '</svg>';

  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}
